import type { Api } from './Api';
import type {
  BotSettings,
  BotStatus,
  BotSummary,
  DesiredState,
  Entitlement,
  IntelligenceMapTarget,
  IntelligenceRequest,
  ModuleKey,
  SupportMessage
} from '../domain/types';
import { mergeSettings } from '../domain/types';
import type { DlIntelligenceStatus, RegionSeedResult, ShieldWatchEvent } from '../domain/intelligence';
import { mapDlIntelligenceStatus, mapRegionSeedResult, mapShieldWatchEvents } from '../domain/intelligence';
import { supabase } from './supabaseClient';

// Multi-instancia (0.6, doc 11): la fila del bot es (user_id, instance_id).
// Con la base SIN la migración 008 las columnas no existen: cada operación
// degrada una vez al modo clásico y recuerda el veredicto (sin reintentos por
// sondeo) — espejo de la degradación del cliente (control_plane_client).
let instanceColumns = true;

async function upsertBotRow(fields: Record<string, unknown>, instanceId?: string): Promise<void> {
  const { data: userData } = await supabase().auth.getUser();
  const userId = userData.user?.id;
  if (instanceColumns && instanceId) {
    const { error } = await supabase()
      .from('downloadable_bots')
      .upsert({ user_id: userId, instance_id: instanceId, ...fields }, { onConflict: 'user_id,instance_id' });
    if (!error) return;
    instanceColumns = false; // base sin migrar: modo clásico a partir de aquí
  }
  const { error } = await supabase()
    .from('downloadable_bots')
    .upsert({ user_id: userId, ...fields }, { onConflict: 'user_id' });
  if (error) throw error;
}

const SUPPORT_BUCKET = 'support-attachments';

// Caché de URLs firmadas de adjuntos: el chat se sondea cada pocos segundos y
// sin esto cada refetch pediría a Storage una firma nueva por adjunto.
const signedUrlCache = new Map<string, { url: string; expiresAt: number }>();

async function signedAttachmentUrl(path: string | null): Promise<string | null> {
  if (!path) return null;
  const cached = signedUrlCache.get(path);
  if (cached && cached.expiresAt > Date.now()) return cached.url;
  const { data, error } = await supabase().storage.from(SUPPORT_BUCKET).createSignedUrl(path, 3600);
  if (error || !data?.signedUrl) return null;
  signedUrlCache.set(path, { url: data.signedUrl, expiresAt: Date.now() + 50 * 60_000 });
  return data.signedUrl;
}

// Fila anidada de PostgREST → dominio (doc 13 §5; espejo del hosted).
function mapIntelligenceRequest(row: Record<string, unknown>): IntelligenceRequest {
  const nested = (row.intelligence_request_results as Array<{ map_targets: Record<string, unknown> | null }> | null) ?? [];
  const results: IntelligenceMapTarget[] = nested
    .map((item) => item?.map_targets)
    .filter((t): t is Record<string, unknown> => !!t)
    .map((t) => ({
      playerName: (t.player_name as string | null) ?? null,
      allianceTag: (t.alliance_tag as string | null) ?? null,
      allianceName: (t.alliance_name as string | null) ?? null,
      power: (t.power as number | null) ?? null,
      castleX: (t.castle_x as number | null) ?? null,
      castleY: (t.castle_y as number | null) ?? null,
      lastSeenAt: (t.last_seen_at as string | null) ?? null,
      confidence: (t.confidence as number | null) ?? null
    }));
  return {
    id: String(row.id),
    playerName: (row.player_name as string) ?? '',
    regionId: (row.region_id as string | null) ?? null,
    queryType: (row.query_type as string | null) ?? null,
    status: (row.status as IntelligenceRequest['status']) ?? 'pending',
    error: (row.error as string | null) ?? null,
    createdAt: (row.created_at as string) ?? '',
    updatedAt: (row.updated_at as string | null) ?? null,
    hiddenAt: (row.user_hidden_at as string | null) ?? null,
    results
  };
}

const DEFAULT_MODULES: Record<ModuleKey, boolean> = { collect: true, train_troops: false, auto_hunt: false };

// Adaptador de PRODUCCIÓN. Auth = Supabase Auth. Datos del propio usuario por RLS
// (D1 camino 1). Entitlement/checkout por la pasarela API (D1 camino 2). La
// service-role JAMÁS aquí: las tablas están protegidas por RLS y el checkout lo
// firma una Netlify Function server-side.
export const supabaseApi: Api = {
  mode: 'supabase',

  async getSession() {
    const { data } = await supabase().auth.getUser();
    const u = data.user;
    return u ? { id: u.id, email: u.email ?? '' } : null;
  },

  async signInWithEmail(email) {
    const { error } = await supabase().auth.signInWithOtp({
      email,
      options: { emailRedirectTo: window.location.origin }
    });
    if (error) throw error;
  },

  async signInWithGoogle() {
    const { error } = await supabase().auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: window.location.origin }
    });
    if (error) throw error;
  },

  async signOut() {
    await supabase().auth.signOut();
  },

  async getEntitlement(): Promise<Entitlement> {
    const { data, error } = await supabase()
      .from('downloadable_subscriptions')
      .select('status, plan, current_period_end, stripe_subscription_id')
      .maybeSingle();
    if (error) throw error;
    if (!data) return { status: 'inactive', plan: null, hasRecord: false };
    return {
      status: data.status as Entitlement['status'],
      plan: (data.plan as Entitlement['plan']) ?? null,
      currentPeriodEnd: data.current_period_end as string | null,
      hasRecord: true,
      viaStripe: !!data.stripe_subscription_id
    };
  },

  async startCheckout() {
    const { data } = await supabase().auth.getSession();
    const token = data.session?.access_token ?? '';
    const res = await fetch('/.netlify/functions/checkout', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` }
    });
    if (!res.ok) throw new Error('No se pudo iniciar el pago');
    return (await res.json()) as { url: string };
  },

  async startTrial() {
    // D16: alta del trial de 5 días SIN tarjeta (server-side, con anti-abuso).
    const { data } = await supabase().auth.getSession();
    const token = data.session?.access_token ?? '';
    const res = await fetch('/.netlify/functions/start-trial', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` }
    });
    if (!res.ok) {
      const detail = await res.json().catch(() => null);
      throw new Error(detail?.error === 'trial_already_used' ? 'trial_already_used' : 'trial_failed');
    }
    const body = (await res.json()) as { trial_end: string };
    return { trialEnd: body.trial_end };
  },

  async startBillingPortal() {
    const { data } = await supabase().auth.getSession();
    const token = data.session?.access_token ?? '';
    const res = await fetch('/.netlify/functions/billing-portal', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` }
    });
    if (!res.ok) {
      // Códigos, no frases (0.4 p7): la UI los traduce. `no_stripe_customer` =
      // licencia concedida a mano, sin cliente de Stripe detrás — no es un error.
      const detail = await res.json().catch(() => null);
      throw new Error(detail?.error === 'no_stripe_customer' ? 'no_stripe_customer' : 'billing_portal_failed');
    }
    return (await res.json()) as { url: string };
  },

  async getBotStatus(instanceId?: string) {
    // Con instanceId: la fila de ESE bot. Sin él (pill de la cabecera, compat
    // mono): el bot con reporte más reciente — maybeSingle() petaría con N
    // filas tras la migración 008.
    let query = supabase()
      .from('downloadable_bots')
      .select('running, state, text, modules, desired, settings, updated_at, last_report_at, client_version, capture_b64, capture_at, snapshots, status_code, status_params');
    if (instanceId && instanceColumns) {
      query = query.eq('instance_id', instanceId);
    }
    const { data: rows, error } = await query
      .order('last_report_at', { ascending: false, nullsFirst: false })
      .limit(1);
    if (error) {
      if (instanceId && instanceColumns) {
        instanceColumns = false;
        return this.getBotStatus();
      }
      throw error;
    }
    const data = rows?.[0] ?? null;
    if (!data) {
      return {
        running: false,
        state: 'idle',
        text: 'Sin cliente conectado.',
        statusCode: 'no_client',
        desired: 'stop',
        modules: { ...DEFAULT_MODULES },
        settings: mergeSettings(null)
      };
    }
    return {
      running: !!data.running,
      state: (data.state as string) ?? 'idle',
      text: (data.text as string) ?? '',
      desired: (data.desired as DesiredState) ?? 'stop',
      modules: (data.modules as Record<ModuleKey, boolean>) ?? { ...DEFAULT_MODULES },
      settings: mergeSettings(data.settings as Partial<BotSettings> | null),
      updatedAt: data.updated_at as string | null,
      lastReportAt: (data.last_report_at as string | null) ?? null,
      clientVersion: (data.client_version as string | null) ?? null,
      captureB64: (data.capture_b64 as string | null) ?? null,
      captureAt: (data.capture_at as string | null) ?? null,
      snapshots: (data.snapshots as BotStatus['snapshots']) ?? null,
      statusCode: (data.status_code as string | null) ?? null,
      statusParams: (data.status_params as BotStatus['statusParams']) ?? null
    };
  },

  async setDesired(desired, modules, instanceId) {
    await upsertBotRow({ desired, modules }, instanceId);
  },

  async saveModules(modules, instanceId) {
    // Solo módulos: NO toca `desired` (guardar módulos no debe arrancar/parar el bot).
    await upsertBotRow({ modules }, instanceId);
  },

  async saveSettings(settings: BotSettings, instanceId) {
    await upsertBotRow({ settings }, instanceId);
  },

  // --- flota (0.6, doc 11): un bot por instancia de BlueStacks ---
  async listBots(): Promise<BotSummary[]> {
    if (instanceColumns) {
      const { data, error } = await supabase()
        .from('downloadable_bots')
        .select('instance_id, instance_label, running, state, status_code, status_params, text, desired, last_report_at, client_version')
        .order('instance_label', { ascending: true, nullsFirst: false });
      if (!error) {
        return (data ?? []).map((row) => ({
          instanceId: (row.instance_id as string) ?? 'default',
          instanceLabel: (row.instance_label as string | null) ?? null,
          running: !!row.running,
          state: (row.state as string) ?? 'idle',
          statusCode: (row.status_code as string | null) ?? null,
          statusParams: (row.status_params as BotSummary['statusParams']) ?? null,
          text: (row.text as string) ?? '',
          desired: (row.desired as DesiredState) ?? 'stop',
          lastReportAt: (row.last_report_at as string | null) ?? null,
          clientVersion: (row.client_version as string | null) ?? null
        }));
      }
      instanceColumns = false; // base sin la migración 008
    }
    // Modo clásico: como mucho una fila; se presenta como el bot 'default'.
    const { data, error } = await supabase()
      .from('downloadable_bots')
      .select('running, state, status_code, status_params, text, desired, last_report_at, client_version')
      .limit(1);
    if (error) throw error;
    const row = data?.[0];
    if (!row) return [];
    return [{
      instanceId: 'default',
      instanceLabel: null,
      running: !!row.running,
      state: (row.state as string) ?? 'idle',
      statusCode: (row.status_code as string | null) ?? null,
      statusParams: (row.status_params as BotSummary['statusParams']) ?? null,
      text: (row.text as string) ?? '',
      desired: (row.desired as DesiredState) ?? 'stop',
      lastReportAt: (row.last_report_at as string | null) ?? null,
      clientVersion: (row.client_version as string | null) ?? null
    }];
  },

  async deleteBot(instanceId: string) {
    const { data: userData } = await supabase().auth.getUser();
    const userId = userData.user?.id;
    const { error } = await supabase()
      .from('downloadable_bots')
      .delete()
      .eq('user_id', userId)
      .eq('instance_id', instanceId);
    if (error) throw error;
  },

  // --- Intelligence (doc 13): mismo patrón que el hosted, vía RLS + RPCs ---
  async listIntelligenceRequests(): Promise<IntelligenceRequest[]> {
    const { data, error } = await supabase()
      .from('intelligence_requests')
      .select(
        'id,region_id,player_name,query_type,status,error,created_at,updated_at,completed_at,user_hidden_at,' +
        'intelligence_request_results(map_targets(player_name,alliance_tag,alliance_name,power,castle_x,castle_y,last_seen_at,confidence))'
      )
      .order('created_at', { ascending: false })
      .limit(25);
    if (error) throw error;
    return ((data as unknown as Record<string, unknown>[] | null) || []).map(mapIntelligenceRequest);
  },

  async submitIntelligenceRequest(playerName: string, regionId?: string): Promise<string> {
    const { data, error } = await supabase().rpc('submit_intelligence_request', {
      player_name: playerName,
      search_region_id: regionId?.trim() || null
    });
    if (error) throw error;
    return String(data);
  },

  async submitIntelligenceRegionRequest(regionId: string): Promise<string> {
    // Región completa (doc 13 §9): RPC aditiva; misma cuota diaria que las
    // búsquedas por nombre. Si la región no existe → error 'unknown region'.
    const { data, error } = await supabase().rpc('submit_intelligence_region_request', {
      search_region_id: regionId.trim()
    });
    if (error) throw error;
    return String(data);
  },

  async deleteIntelligenceRequest(requestId: string): Promise<void> {
    const { error } = await supabase().rpc('hide_intelligence_request', { target_request_id: requestId });
    if (error) throw error;
  },

  // --- Intelligence 0.6.4 (doc 17): RPCs dl_* con economía de tokens ---
  async dlIntelligenceStatus(regionId?: string): Promise<DlIntelligenceStatus> {
    // Una llamada para todo el pintado: saldo, gratis de hoy, costes y — si se
    // pasa la región del mapeo — su cobertura (scanned/total + hoy).
    const { data, error } = await supabase().rpc('dl_intelligence_status', {
      p_region_id: regionId?.trim() || null
    });
    if (error) throw error;
    return mapDlIntelligenceStatus(data);
  },

  async dlSubmitIntelligenceRequest(playerName: string, regionId?: string): Promise<string> {
    // Sustituye a submit_intelligence_request en este repo (doc 17 §6.5):
    // 1 gratis/día UTC y después cobra tokens del ledger (atómico, server-side).
    const { data, error } = await supabase().rpc('dl_submit_intelligence_request', {
      player_name: playerName,
      search_region_id: regionId?.trim() || null
    });
    if (error) throw error;
    return String(data);
  },

  async dlSubmitRegionRequest(regionId: string): Promise<string> {
    // Volcado de región completa con tokens (sustituye a
    // submit_intelligence_region_request en este repo).
    const { data, error } = await supabase().rpc('dl_submit_intelligence_region_request', {
      search_region_id: regionId.trim()
    });
    if (error) throw error;
    return String(data);
  },

  async ensureRegionSeeded(regionId: string): Promise<RegionSeedResult> {
    // Siembra self-serve (idempotente): valida el formato, clona la plantilla si
    // la región no existe y devuelve el total de celdas — el botón "Comprobar
    // región" de los ajustes de mapeo pinta el resultado.
    const { data, error } = await supabase().rpc('ensure_region_seeded', {
      p_region_id: regionId.trim()
    });
    if (error) throw error;
    return mapRegionSeedResult(data);
  },

  async dlShieldWatchHistory(limit = 20): Promise<ShieldWatchEvent[]> {
    // Historial de cambios de estado de la vigilancia (RLS: solo los propios).
    const { data, error } = await supabase().rpc('dl_shield_watch_history', {
      p_limit: limit
    });
    if (error) throw error;
    return mapShieldWatchEvents(data);
  },

  async dlShieldWatchCapture(instanceId: string, stamp: number, state: string): Promise<string | null> {
    const { data, error } = await supabase().rpc('dl_shield_watch_capture', {
      p_instance_id: instanceId,
      p_at: stamp,
      p_state: state
    });
    if (error) throw error;
    const b64 = typeof data === 'string' ? data.trim() : '';
    return b64 ? `data:image/jpeg;base64,${b64}` : null;
  },

  async requestCapture(instanceId) {
    // Marca de petición: el cliente la ve en su sondeo y sube una captura fresca
    // (responde actualizando capture_b64/capture_at). NO toca desired.
    await upsertBotRow({ capture_requested_at: new Date().toISOString() }, instanceId);
  },

  // --- Soporte (doc 14): hilo único por usuario vía RLS; el aviso a Telegram
  // lo envía la función support-notify (server-side, service-role) ---
  async listSupportMessages(): Promise<SupportMessage[]> {
    const { data, error } = await supabase()
      .from('downloadable_support_messages')
      .select('id,sender,body,attachment_path,read_at,created_at')
      .order('created_at', { ascending: true })
      .limit(200);
    if (error) throw error;
    const rows = (data as Record<string, unknown>[] | null) ?? [];
    return Promise.all(
      rows.map(async (row) => ({
        id: String(row.id),
        sender: row.sender === 'support' ? ('support' as const) : ('user' as const),
        body: (row.body as string) ?? '',
        attachmentUrl: await signedAttachmentUrl((row.attachment_path as string | null) ?? null),
        createdAt: (row.created_at as string) ?? '',
        readAt: (row.read_at as string | null) ?? null
      }))
    );
  },

  async sendSupportMessage(body: string, attachment?: File | null): Promise<void> {
    const { data: userData } = await supabase().auth.getUser();
    const userId = userData.user?.id;
    if (!userId) throw new Error('unauthenticated');

    // 1) adjunto (opcional) a la carpeta del usuario del bucket privado
    let attachmentPath: string | null = null;
    if (attachment) {
      const ext = (attachment.name.split('.').pop() || 'png').toLowerCase().replace(/[^a-z0-9]/g, '') || 'png';
      attachmentPath = `${userId}/${Date.now()}.${ext}`;
      const { error: uploadError } = await supabase()
        .storage.from(SUPPORT_BUCKET)
        .upload(attachmentPath, attachment, { contentType: attachment.type || 'image/png' });
      if (uploadError) throw uploadError;
    }

    // 2) el mensaje, por RLS (solo puede insertarse como él mismo y sender='user')
    const { data: inserted, error } = await supabase()
      .from('downloadable_support_messages')
      .insert({ user_id: userId, sender: 'user', body, attachment_path: attachmentPath })
      .select('id')
      .single();
    if (error) throw error;

    // 3) aviso a Telegram (server-side). Si falla, el mensaje YA está guardado:
    // no se rompe el envío por un hipo del aviso.
    try {
      const { data: sessionData } = await supabase().auth.getSession();
      const token = sessionData.session?.access_token ?? '';
      await fetch('/.netlify/functions/support-notify', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ message_id: inserted.id })
      });
    } catch (err) {
      console.warn('[soporte] aviso a Telegram no enviado:', err);
    }
  },

  async markSupportRead(): Promise<void> {
    // RLS limita a su hilo; el grant de columna limita el update a read_at.
    const { error } = await supabase()
      .from('downloadable_support_messages')
      .update({ read_at: new Date().toISOString() })
      .eq('sender', 'support')
      .is('read_at', null);
    if (error) throw error;
  },

  async notifySignup(): Promise<void> {
    // Best-effort: si falla, no se molesta al usuario (el dedupe es del server).
    try {
      const { data } = await supabase().auth.getSession();
      const token = data.session?.access_token ?? '';
      if (!token) return;
      await fetch('/.netlify/functions/signup-notify', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch {
      /* silencioso */
    }
  },

  async logClientDownload(version: string): Promise<void> {
    // Embudo (017): quién pulsó "Descargar cliente" y qué versión se le ofreció.
    // user_id lo pone el default de la tabla (auth.uid()); RLS impide suplantar.
    // Best-effort: cualquier fallo se traga — el dato jamás bloquea la descarga.
    try {
      await supabase()
        .from('downloadable_download_clicks')
        .insert({ version: version || null });
    } catch {
      /* silencioso */
    }
  }
};
